// mapNode.js — Report embed block ("window") into the report's single shared map.
// Map content (layers/markings/views, plus the shared DTG range — ticket 02) lives in the
// report's one shared Y.XmlFragment (see ../../../map/mapFragmentInit.js's
// getSharedMapFragment/initializeSharedMapFragment) — never in this node's own attrs. This
// node's attrs hold only per-embed "window" state: which view/DTG scrub point/basemap/etc. this
// particular embed is currently showing into that shared map.

import {generateUUID} from '../../helpers/utils';
import {DEFAULT_BASEMAP, OVERLAY_TYPES} from '../../../map/schema/mapSchema';

// Ticket 01 (map-node-dtg-view-audit): parses a `data-dtg-*-ms` attribute value into an epoch-ms
// number, defaulting to null (absent/invalid) rather than throwing — legacy documents predating
// this ticket never had these attributes.
function parseDtgMsAttr(value) {
    if (value == null || value === '') return null;
    const ms = Number(value);
    return Number.isFinite(ms) ? ms : null;
}

export const mapNode = {
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,
    // Ticket 04 (map-node-dtg-view-audit): `nodeId` must be unique per node instance — it
    // identifies this embed's own window state (view/DTG-scrub/basemap/etc, NOT a fragment it
    // owns — see the shared-map-data ticket 01 rework above). blockIdPlugin's generalized
    // paste/duplicate mechanism reads this to regenerate `nodeId` (in addition to the universal
    // `blockId`) whenever a mapNode is pasted, or backfilled if somehow created without one.
    uniqueIdAttrs: ['nodeId'],
    // Ticket 01 (shared-map-data): previously initialized a fresh empty per-node Y.XmlFragment
    // for the regenerated id here. Now that every embed reads/writes the one shared map
    // fragment, a pasted/duplicated/backfilled node has nothing per-node left to initialize —
    // it's simply a new window (its own fresh nodeId, default window attrs) onto the same
    // shared map every other embed in the report already sees. No `onRegenerateId` hook needed.
    attrs: {
        // Stable UUID identifying this embed's own window state.
        // Generated once on insertion and never changes.
        nodeId: {default: null},
        viewId: {default: null},
        classificationLevel: {default: 'U'},
        distribution: {default: []},
        compartment: {default: []},
        // Ticket 06: last successfully-captured map+symbols preview image (mirrors the Yjs
        // mapPreviews Y.Map entry for this node's nodeId at serialization time). Only used by
        // static/read-only renders (e.g. ReadonlyProseMirrorRenderer) that have no live Yjs doc to
        // read from directly — the live collaborative MapNodeView ignores this attr entirely and
        // renders straight from mapPreviewsYMap.js via the shared MapPreviewQueueService.
        snapshotThumbUrl: {default: null},
        // ticket 04 (layer-panel-rework): free-text caption shown below the preview in place of
        // the removed auto-generated layer legend — matches `figure`'s `caption` attr convention.
        caption: {default: ''},
        // Ticket 01 (map-node-dtg-view-audit): per-node DTG (date-time-group) scrub position —
        // this embed's own position on the shared timeline. Naming mirrors useDtgRange's valueMs
        // convention. `null` (the default) means "no DTG selected" — see ticket 02 for the
        // render-side contract.
        // shared-map-data ticket 02: the `[dtgStartMs, dtgEndMs]` *range* this scrub point lives
        // within moved to the shared map doc's `map_meta` node (see mapSchema.js) — every embed
        // in a report shows the same range, so it no longer belongs here.
        dtgSelectedMs: {default: null},
        // shared-map-data ticket 03: `title`/`basemapStyleId`/`activeGridId`/`overlayType`/
        // `currentDtgMs` used to live on the shared map doc's `map_meta` node, but they're
        // "window" settings — which basemap/grid/overlay this particular embed is showing, and
        // (while overlayType is 'satellite') its own satellite-imagery-as-of cursor — not shared
        // map content, so each embed keeps its own independent copy here instead.
        title: {default: 'Untitled Map'},
        basemapStyleId: {default: DEFAULT_BASEMAP},
        activeGridId: {default: null},
        overlayType: {default: OVERLAY_TYPES.MAPBOX},
        currentDtgMs: {default: null},
    },
    parseDOM: [{
        tag: "div[data-type='map-node']",
        getAttrs(dom) {
            const distribution = (() => {
                try { return JSON.parse(dom.getAttribute('data-distribution') || '[]'); } catch { return []; }
            })();
            const compartment = (() => {
                try { return JSON.parse(dom.getAttribute('data-compartment') || '[]'); } catch { return []; }
            })();
            return {
                nodeId: dom.getAttribute('data-node-id') || generateUUID(),
                viewId: dom.getAttribute('data-view-id') || null,
                classificationLevel: dom.getAttribute('data-classification-level') || 'U',
                distribution,
                compartment,
                snapshotThumbUrl: dom.getAttribute('data-snapshot-thumb-url') || null,
                caption: dom.getAttribute('data-caption') || '',
                dtgSelectedMs: parseDtgMsAttr(dom.getAttribute('data-dtg-selected-ms')),
                title: dom.getAttribute('data-title') || 'Untitled Map',
                basemapStyleId: dom.getAttribute('data-basemap') || DEFAULT_BASEMAP,
                activeGridId: dom.getAttribute('data-active-grid') || null,
                // Back-compat: legacy DOM nodes never had data-overlay-type — default cleanly
                // to 'mapbox' (also guards against an unrecognized/corrupt value).
                overlayType: dom.getAttribute('data-overlay-type') === OVERLAY_TYPES.SATELLITE
                    ? OVERLAY_TYPES.SATELLITE : OVERLAY_TYPES.MAPBOX,
                currentDtgMs: parseDtgMsAttr(dom.getAttribute('data-current-dtg-ms')),
            };
        },
    }],
    toDOM(node) {
        const {
            nodeId, viewId, classificationLevel, distribution, compartment, snapshotThumbUrl, caption,
            dtgSelectedMs, title, basemapStyleId, activeGridId, overlayType, currentDtgMs,
        } = node.attrs;

        const attrs = {
            'data-type': 'map-node',
            class: 'pm-map-node',
            'data-node-id': nodeId,
        };
        if (viewId) attrs['data-view-id'] = viewId;
        attrs['data-classification-level'] = classificationLevel;
        if (distribution?.length > 0) attrs['data-distribution'] = JSON.stringify(distribution);
        if (compartment?.length > 0) attrs['data-compartment'] = JSON.stringify(compartment);
        if (snapshotThumbUrl) attrs['data-snapshot-thumb-url'] = snapshotThumbUrl;
        if (caption) attrs['data-caption'] = caption;
        if (dtgSelectedMs != null) attrs['data-dtg-selected-ms'] = String(dtgSelectedMs);
        attrs['data-title'] = title;
        attrs['data-basemap'] = basemapStyleId;
        if (activeGridId) attrs['data-active-grid'] = activeGridId;
        attrs['data-overlay-type'] = overlayType || OVERLAY_TYPES.MAPBOX;
        if (currentDtgMs != null) attrs['data-current-dtg-ms'] = String(currentDtgMs);

        const content = snapshotThumbUrl ? ['img', {src: snapshotThumbUrl, alt: ''}] : 'Map';
        return ['div', attrs, content];
    },
};
